import DtoolsOptionSectionActions, {
  type DtoolsOptionSectionActionsProps,
} from "@/components/sales/dtools/DtoolsOptionSectionActions";
import { cn } from "@/lib/utils";
import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import { formatCustomerDiscountAmount } from "@shared/dtoolsCustomerProposalSummaryRows";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";

export type ConsolidatedSystemSummaryRow = {
  key: string;
  label: string;
  amount: number;
  /** Pricing used for this system's approve dialog when individual approval is on. */
  customerPricing?: ProposalCustomerPricingDisplay;
};

type Props = {
  systemRows: ConsolidatedSystemSummaryRow[];
  /** Whole-proposal pricing (also used for grand total / discount when present). */
  proposalPricing?: ProposalCustomerPricingDisplay | null;
  /** Format money; defaults to formatDtoolsMoney. */
  formatMoney?: (n: number) => string;
  allowIndividualSystemApproval: boolean;
  /** When true (multiple-options proposals), omit combined discount and grand total rows. */
  hideCombinedGrandTotal?: boolean;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  className?: string;
  testId?: string;
};

/** One consolidated System Summary table — name left, price right, optional row Approve. */
export default function ProposalConsolidatedSystemSummaryTable({
  systemRows,
  proposalPricing,
  formatMoney = formatDtoolsMoney,
  allowIndividualSystemApproval,
  hideCombinedGrandTotal = false,
  optionActions,
  className,
  testId = "proposal-consolidated-system-summary",
}: Props) {
  const positiveSystems = systemRows.filter((row) => Number.isFinite(row.amount) && row.amount > 0.004);
  if (positiveSystems.length === 0) return null;

  const systemsTotal = positiveSystems.reduce((sum, row) => sum + row.amount, 0);
  const showDiscount =
    !hideCombinedGrandTotal &&
    proposalPricing?.showFinalAgreedPrice === true &&
    Number.isFinite(proposalPricing.proposalTotal) &&
    Number.isFinite(proposalPricing.finalAgreedPrice) &&
    proposalPricing.proposalTotal - proposalPricing.finalAgreedPrice > 0.004;
  const discountAmount = showDiscount
    ? proposalPricing!.proposalTotal - proposalPricing!.finalAgreedPrice
    : 0;
  const showGst =
    !hideCombinedGrandTotal &&
    proposalPricing?.showGst === true &&
    Number.isFinite(proposalPricing.gstAmount) &&
    proposalPricing.gstAmount > 0.004;
  const grandTotal =
    showGst && Number.isFinite(proposalPricing?.finalAmountPayable)
      ? proposalPricing!.finalAmountPayable
      : proposalPricing?.showFinalAgreedPrice === true && Number.isFinite(proposalPricing.finalAgreedPrice)
      ? proposalPricing.finalAgreedPrice
      : systemsTotal;
  const grandLabel =
    showGst
      ? "Final Amount Payable"
      : proposalPricing?.showFinalAgreedPrice === true
        ? "Final Price"
        : "Grand Total";

  const showRowApprove = allowIndividualSystemApproval && Boolean(optionActions);
  const showWholeApprove = !allowIndividualSystemApproval && Boolean(optionActions);

  return (
    <section className={cn("space-y-4", className)} data-testid={testId} style={{ fontFamily: PROPOSAL_COPY_FONT }}>
      <div
        className="overflow-hidden rounded-xl border border-[#1A2744]/10 bg-white"
        data-testid="proposal-system-summary-table"
      >
        <div className="divide-y divide-[#1A2744]/8">
          {positiveSystems.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5"
              data-testid={`proposal-system-summary-row-${row.key}`}
            >
              <span className="min-w-0 flex-1 text-left text-sm font-medium text-[#1A2744]">{row.label}</span>
              <div className={cn("flex shrink-0 items-center", showRowApprove ? "gap-3" : "gap-0")}>
                <span className="text-right text-sm font-semibold tabular-nums text-[#1A2744]">
                  {formatMoney(row.amount)}
                </span>
                {showRowApprove ? (
                  <DtoolsOptionSectionActions
                    optionLabel={row.label}
                    {...optionActions!}
                    customerPricing={row.customerPricing ?? proposalPricing ?? optionActions!.customerPricing}
                    variant="approve-inline"
                    multiOptionApproval
                  />
                ) : null}
              </div>
            </div>
          ))}

          {showDiscount ? (
            <div
              className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5"
              data-testid="proposal-system-summary-discount"
            >
              <span className="text-sm text-[#1A2744]/65">Discount</span>
              <span className="text-sm tabular-nums text-[#1A2744]/75">
                {formatCustomerDiscountAmount(discountAmount, formatMoney)}
              </span>
            </div>
          ) : null}

          {showGst ? (
            <div
              className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5"
              data-testid="proposal-system-summary-gst"
            >
              <span className="text-sm text-[#1A2744]/65">GST (9%)</span>
              <span className="text-sm tabular-nums text-[#1A2744]/75">
                {formatMoney(proposalPricing!.gstAmount)}
              </span>
            </div>
          ) : null}

          {!hideCombinedGrandTotal ? (
            <div
              className="flex items-center justify-between gap-3 bg-[#FAFAF7] px-4 py-3.5 sm:px-5"
              data-testid="proposal-system-summary-grand-total"
            >
              <span className="text-sm font-semibold text-[#1A2744]">{grandLabel}</span>
              <span className="text-right text-base font-bold tabular-nums text-[#1A2744]">
                {formatMoney(grandTotal)}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {showWholeApprove ? (
        <DtoolsOptionSectionActions
          optionLabel="this proposal"
          {...optionActions!}
          customerPricing={proposalPricing ?? optionActions!.customerPricing}
          variant="approve-only"
          multiOptionApproval
        />
      ) : null}
    </section>
  );
}
